<template>
  <CModal :show="true" :closeOnBackdrop="false" :centered="true">
    <template v-if="order" #header>
      <h5 class="modal-title">
        {{ $t("role.orders.manage.boxesModal.title") }} #{{ order.order.id }}
      </h5>
    </template>

    <div v-if="boxes.length < 1" class="mb-3">
      {{ $t("role.orders.manage.boxesModal.noBoxes") }}
    </div>
    <div v-else>
      <div
        class="
          d-flex
          w-100
          flex-row
          justify-content-end
          align-items-center
          mb-2
        "
      >
        <CButton
          color="primary"
          size="sm"
          @click="printLabelsAll(order.order.id)"
        >
          <font-awesome-icon icon="print" />
          {{ $t("role.orders.manage.boxesModal.printAll") }}
        </CButton>
      </div>

      <table class="table table-bordered bg-white">
        <thead>
          <tr>
            <th scope="col">ID</th>
            <th scope="col">
              {{ $t("role.orders.manage.boxesModal.label") }}
            </th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="box in boxes" :key="box.id">
            <td scope="row">
              {{ box.id }}
              <span v-if="box.extra.submitted" class="ml-2">
                <font-awesome-icon icon="truck" class="text-primary" />
              </span>
              <span v-else class="ml-2">
                <font-awesome-icon icon="clock" class="text-primary" />
              </span>
            </td>
            <td>
              <small class="text-muted">
                {{ order.order.family.name }} <br />
                {{ order.order.family.position.address }} <br />
                {{ order.order.family.position.postal_code }}
                {{ order.order.family.position.city }} <br />
                {{ formattedDeliveryDate }}
              </small>
            </td>
            <td>
              <div
                class="
                  d-flex
                  w-100
                  flex-row
                  justify-content-end
                  align-items-center
                "
              >
                <CButton
                  v-if="!box.extra.submitted"
                  color="danger"
                  size="sm"
                  @click="deleteBox(box.id)"
                  class="mr-1"
                >
                  <font-awesome-icon icon="trash-alt" />
                </CButton>
                <CButton color="primary" size="sm" @click="printLabel(box.pdf)">
                  <font-awesome-icon icon="print" />
                </CButton>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <h5>{{ $t("role.orders.manage.boxesModal.addButton") }}</h5>
    <div class="form-group">
      <label>{{ $t("role.orders.manage.boxesModal.numberBoxes") }}</label>
      <CInput
        class="mb-0 mr-1"
        v-model="BoxesForm.numberBoxes"
        type="number"
        min="1"
        placeholder="1"
      />
    </div>
    <template #footer>
      <CButton
        color="primary"
        variant="outline"
        @click.prevent="$emit('close')"
        >{{ $t("role.orders.manage.addProductModal.btnCancel") }}</CButton
      >
      <vue-ladda
        :loading="loading"
        data-style="zoom-in"
        button-class="btn btn-primary px-4"
        @click.prevent="boxesFormOnSubmit"
        >{{ $t("role.orders.manage.addProductModal.btnAddProduct") }}</vue-ladda
      >
    </template>
  </CModal>
</template>

<script>
import errorResponse from "../../../../../../helpers/error";
import {
  apiPost,
  apiGet,
  apiDelete,
} from "../../../../../../helpers/apiHelper";
import { formatDate } from "../../../../../../helpers/common";

export default {
  name: "OrderTableBoxesModal",
  props: {
    order: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      AddFormError: errorResponse(),
      BoxesForm: {
        numberBoxes: 1,
      },
      boxes: [],
    };
  },
  methods: {
    async deleteBox(boxId) {
      const confirmed = confirm(
        this.$t("role.orders.manage.boxesModal.deleteConfirmMsg")
      );

      if (!confirmed) {
        return;
      }

      const response = await apiDelete(
        "packaging_boxes",
        [boxId],
        [this.$store.state.role.id, this.order.order.id]
      );

      if (response && response.status === 200) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("role.orders.manage.boxesModal.deleteSuccess"),
          color: "success",
          autohide: true,
        });

        this.getBoxes();
      } else {
        console.error("Unexpected response:", response);
      }
    },

    printLabel(pdf) {
      window.open(window.env.LOONITY_BASE_API_URL + pdf, "_blank");
    },

    async printLabelsAll() {
      try {
        // Collect all PDF URLs
        let pdfs = [];

        this.boxes.forEach((box) => {
          if (box.pdf) {
            pdfs.push(window.env.LOONITY_BASE_API_URL + box.pdf);
          }
        });

        if (pdfs.length === 0) {
          console.warn("No PDFs to merge");
          return;
        }

        // Import pdf-lib dynamically
        const { PDFDocument } = await import("pdf-lib");
        const mergedPdf = await PDFDocument.create();

        // Download and merge each PDF
        for (const pdfUrl of pdfs) {
          try {
            const response = await fetch(pdfUrl);
            const pdfBytes = await response.arrayBuffer();
            const pdf = await PDFDocument.load(pdfBytes);
            const copiedPages = await mergedPdf.copyPages(
              pdf,
              pdf.getPageIndices()
            );
            copiedPages.forEach((page) => mergedPdf.addPage(page));
          } catch (error) {
            console.error(`Failed to process PDF: ${pdfUrl}`, error);
          }
        }

        // Generate merged PDF
        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);

        // Download in new tab
        window.open(blobUrl, "_blank");

        // Clean up
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      } catch (error) {
        console.error("Error merging PDFs:", error);
        this.$store.dispatch("toaster/add", {
          title: "Error",
          text: "Failed print labels",
          color: "danger",
          autohide: true,
        });
      }
    },

    async boxesFormOnSubmit() {
      const confirmed = confirm(
        this.$t("role.orders.manage.boxesModal.addConfirmMsg")
      );

      if (!confirmed) {
        return;
      }

      const requestParams = {
        num: this.BoxesForm.numberBoxes,
      };

      const response = await apiPost("packaging_boxes", requestParams, [
        this.$store.state.role.id,
        this.order.order.id,
      ]);

      if (response && response.status === 201) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("role.orders.manage.boxesModal.addSuccess"),
          color: "success",
          autohide: true,
        });

        this.getBoxes();
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }

      this.$emit("submit", requestParams);
    },

    async getBoxes() {
      const response = await apiGet("packaging_boxes", [
        this.$store.state.role.id,
        this.order.order.id,
      ]);

      if (response && response.status === 200) {
        this.boxes = response.data.payload;
      } else {
        console.error("Unexpected response:", response);
      }
    },

    async getOrderInfo() {},
  },

  computed: {
    formattedDeliveryDate() {
      return formatDate(
        new Date(this.order.order.ddmi.delivery_date),
        "dd.MM.yyyy"
      );
    },
  },

  mounted() {
    this.getBoxes();
  },
};
</script>
